import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import classes from "./SDGDetail.module.css";
import { BaseURL } from "../home/BaseData";
import { HighlightIcon } from "./SDGHighlightIcons";

const TEXT = {
  loading: { uz: "Yuklanmoqda...", en: "Loading...", ru: "Загрузка..." },
  home: { uz: "Bosh sahifa", en: "Home", ru: "Главная" },
  sdg: { uz: "SDG", en: "SDG", ru: "ЦУР" },
  more: { uz: "Batafsil", en: "Learn more", ru: "Подробнее" },
  notFoundTitle: {
    uz: "Ma'lumot topilmadi",
    en: "Not found",
    ru: "Материал не найден",
  },
  notFoundText: {
    uz: "Siz qidirayotgan SDG maqsadi mavjud emas yoki o'chirilgan.",
    en: "The SDG goal you are looking for does not exist or has been removed.",
    ru: "Запрашиваемая цель не существует или была удалена.",
  },
  soon: {
    uz: "Ma'lumot tez kunda joylanadi.",
    en: "Content will be published soon.",
    ru: "Материал будет опубликован в ближайшее время.",
  },
};


/* --- Maqsad rangidan o'qiladigan variantlarni hisoblash --- */

const FALLBACK_ACCENT = "#f8c008"; // logodagi sariq — rang berilmagan holat uchun

const toRgb = (hex) => {
  const h = String(hex || "").replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
};

const toHex = ({ r, g, b }) =>
  "#" + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v)))
    .toString(16).padStart(2, "0")).join("");

/** WCAG nisbiy yorqinligi */
const luminance = ({ r, g, b }) => {
  const f = (v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

const contrast = (a, b) => {
  const l1 = Math.max(a, b);
  const l2 = Math.min(a, b);
  return (l1 + 0.05) / (l2 + 0.05);
};

const mix = (rgb, target, k) => ({
  r: rgb.r + (target.r - rgb.r) * k,
  g: rgb.g + (target.g - rgb.g) * k,
  b: rgb.b + (target.b - rgb.b) * k,
});

const BLACK = { r: 0, g: 0, b: 0 };
const WHITE = { r: 255, g: 255, b: 255 };
const NAVY = { r: 19, g: 54, b: 84 };

/** Oq fonda o'qiladigan variant — kerak bo'lsa to'qlashtiriladi */
const inkOnWhite = (rgb) => {
  let c = rgb;
  for (let i = 0; i < 20 && contrast(luminance(c), luminance(WHITE)) < 4.5; i += 1) {
    c = mix(c, BLACK, 0.12);
  }
  return toHex(c);
};

/** To'q ko'k fonda o'qiladigan variant — kerak bo'lsa yoritiladi */
const inkOnNavy = (rgb) => {
  let c = rgb;
  for (let i = 0; i < 20 && contrast(luminance(c), luminance(NAVY)) < 4.5; i += 1) {
    c = mix(c, WHITE, 0.12);
  }
  return toHex(c);
};

/** Rangli fon ustidagi matn: navy yoki oq — qaysi biri ko'proq farq qilsa */
const inkOnAccent = (rgb) => {
  const l = luminance(rgb);
  return contrast(l, luminance(NAVY)) >= contrast(l, luminance(WHITE))
    ? "#0b2338"
    : "#ffffff";
};

const accentVars = (hex) => {
  const rgb = toRgb(hex) || toRgb(FALLBACK_ACCENT);
  return {
    "--accent": toHex(rgb),
    "--accent-ink": inkOnWhite(rgb),
    "--accent-on-dark": inkOnNavy(rgb),
    "--on-accent": inkOnAccent(rgb),
  };
};

/** Matnni xatboshilarga ajratadi — admin oddiy matn kiritadi. */
const paragraphs = (text) =>
  (text || "")
    .split(/\n{1,}/)
    .map((t) => t.trim())
    .filter(Boolean);

/* ------------------------------------------------------------------ */
/*  Qo'shimcha ko'rinishlar: ko'rsatkich bandi, savol-javob, iqtibos.   */
/*  Har biri admin kiritgan ELEMENT turidan chiqadi — alohida maydon    */
/*  yoki sozlama talab qilmaydi.                                        */
/* ------------------------------------------------------------------ */

/** Blok ko'rinish maydoniga kirganda bir marta ishga tushadi. */
const useReveal = () => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return undefined;
    // Eski brauzerda kuzatuvchi yo'q — shunchaki darhol ko'rsatiladi.
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          io.disconnect();
        }
      },
      // Ozgina ko'ringani yetarli: balandligi ekrandan katta band ham
      // "yetarlicha ko'rinmadi" deb sanalmay qolib ketmasin.
      { threshold: 0, rootMargin: "0px 0px -80px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);

  return [ref, seen];
};

/**
 * "74%", "1 200+", "12,5 mln" kabi yozuvdan sonni ajratadi.
 * Son topilmasa null — bunday qiymat sanalmaydi, shunchaki yoziladi.
 * Bo'sh joy faqat uch xonali guruh oldida kelsa ajratgich deb olinadi,
 * shuning uchun "12 mln" dagi "mln" son bilan qo'shilib ketmaydi.
 */
const parseStatValue = (raw) => {
  const m = String(raw || "").match(
    /^(\D*?)(\d+(?:[\s\u00a0]\d{3})*(?:[.,]\d+)?)([\s\S]*)$/
  );
  if (!m) return null;

  const [, prefix, numRaw, suffix] = m;
  const grouped = /[\s\u00a0]/.test(numRaw);
  const cleaned = numRaw.replace(/[\s\u00a0]/g, "");
  const sep = cleaned.includes(",") ? "," : ".";
  const decimals = (cleaned.split(/[.,]/)[1] || "").length;
  const target = parseFloat(cleaned.replace(",", "."));
  if (!Number.isFinite(target)) return null;

  const format = (n) => {
    let out = decimals
      ? n.toFixed(decimals).replace(".", sep)
      : String(Math.round(n));
    if (grouped) {
      const [ip, dp] = out.split(sep);
      out =
        ip.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0") + (dp ? sep + dp : "");
    }
    return out;
  };

  return { prefix, suffix, target, format };
};

/** "18%" -> ["18", "%"]. Son topilmasa butun matn kattada qoladi. */
const splitValue = (raw) => {
  const v = String(raw || "").trim();
  const m = v.match(/^([^A-Za-z%+]*[0-9][0-9\s\u00a0.,]*)(.*)$/);
  if (!m) return [v, ""];
  return [m[1].trim(), m[2].trim()];
};

/** Ko'rsatkich qiymati — ko'ringanda 0 dan o'sha songacha sanaladi. */
const StatValue = ({ value, run }) => {
  const parsed = useMemo(() => parseStatValue(value), [value]);
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (!parsed || !run) return undefined;
    // Harakatni kamaytirish yoqilgan bo'lsa — darhol yakuniy qiymat.
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setShown(parsed.target);
      return undefined;
    }
    const dur = 1100;
    const t0 = performance.now();
    let raf = 0;
    const tick = (t) => {
      const k = Math.min(1, (t - t0) / dur);
      setShown(parsed.target * (1 - Math.pow(1 - k, 3)));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [parsed, run]);

  if (!parsed) return <>{value}</>;
  return (
    <>
      {parsed.prefix}
      {parsed.format(run ? shown : 0)}
      {parsed.suffix}
    </>
  );
};

/** Ko'rsatkichlar bandi — 2-4 ta katta son bir qatorda. */
const StatBand = ({ items, L }) => {
  const [ref, seen] = useReveal();
  return (
    <div
      ref={ref}
      className={`${classes.statBand} ${seen ? classes.statBandIn : ""}`}
    >
      {items.map((s, i) => (
        <div
          key={s.id}
          className={classes.statCell}
          style={{ transitionDelay: `${i * 80}ms` }}
        >
          <span className={classes.statValue}>
            <StatValue value={L(s, "title")} run={seen} />
          </span>
          {L(s, "text") && <span className={classes.statLabel}>{L(s, "text")}</span>}
        </div>
      ))}
    </div>
  );
};

/** Savol-javob — birinchisi ochiq turadi, qolganlari bosilganda ochiladi. */
const FaqList = ({ items, L, idBase }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className={classes.faq}>
      {items.map((q, i) => {
        const isOpen = open === i;
        const panelId = `${idBase}-javob-${q.id}`;
        return (
          <div
            key={q.id}
            className={`${classes.faqItem} ${isOpen ? classes.faqItemOpen : ""}`}
          >
            <button
              type="button"
              className={classes.faqQ}
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setOpen(isOpen ? -1 : i)}
            >
              <span className={classes.faqQText}>{L(q, "title")}</span>
              <span className={classes.faqIcon} aria-hidden="true" />
            </button>
            <div id={panelId} className={classes.faqAWrap}>
              <div className={classes.faqA}>
                {paragraphs(L(q, "text")).map((t, k) => (
                  <p key={k} className={classes.faqAText}>
                    {t}
                  </p>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Ajratilgan kartalar qatori. Har bir karta: belgi, ustki yorliq, sarlavha,
 * matn va pastki yorliq. KATTA SON yozilgan karta to'q fonda ajralib turadi.
 */
const HighlightRow = ({ cards, L }) => {
  const [ref, seen] = useReveal();
  return (
    <div
      ref={ref}
      className={`${classes.hlRow} ${seen ? classes.hlRowIn : ""}`}
    >
      {cards.map((c, i) => {
        const featured = Boolean(c.value);
        const [big, small] = splitValue(c.value);
        const eyebrow = L(c, "eyebrow");
        const title = L(c, "title");
        const text = L(c, "text");
        const badge = L(c, "badge");
        return (
          <article
            key={c.id}
            className={`${classes.hlCard} ${featured ? classes.hlCardFeatured : ""}`}
            style={{ transitionDelay: `${i * 90}ms` }}
          >
            {/* Burchakdagi xira takror — admin tanlagan belgidan chiqadi */}
            {c.icon && (
              <span className={classes.hlWatermark} aria-hidden="true">
                <HighlightIcon name={c.icon} />
              </span>
            )}

            {c.icon && !featured && (
              <span className={classes.hlIcon}>
                <HighlightIcon name={c.icon} />
              </span>
            )}

            {eyebrow && <span className={classes.hlEyebrow}>{eyebrow}</span>}

            {featured && (
              <>
                <span className={classes.hlRule} aria-hidden="true" />
                <span className={classes.hlValue}>
                  <StatValue value={big} run={seen} />
                  {small && <span className={classes.hlValueSuffix}>{small}</span>}
                </span>
              </>
            )}

            {title && <h3 className={classes.hlTitle}>{title}</h3>}
            {text && <p className={classes.hlText}>{text}</p>}
            {badge && (
              <span className={classes.hlBadge}>
                <span className={classes.hlBadgeInner}>{badge}</span>
              </span>
            )}
          </article>
        );
      })}
    </div>
  );
};

const SDGDetail = () => {
  const { identifier } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchGoalDetail = async () => {
      setLoading(true);
      setError(false);
      try {
        const urlsToTry = [
          `${BaseURL.replace(/\/$/, "")}/api/sdg/${identifier}/`,
          `http://127.0.0.1:8001/api/sdg/${identifier}/`,
          `http://127.0.0.1:8000/api/sdg/${identifier}/`,
          `http://localhost:8000/api/sdg/${identifier}/`,
          `http://localhost:8001/api/sdg/${identifier}/`
        ];
        let response = null;
        for (const url of urlsToTry) {
          try {
            response = await axios.get(url);
            if (response && response.data && response.data.goal) break;
          } catch (e) {
            // try next
          }
        }

        if (response && response.data && response.data.goal) {
          setGoal(response.data.goal);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error("Error fetching SDG detail:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (identifier) {
      fetchGoalDetail();
    }
  }, [identifier]);

  const lang = i18n.language || "uz";
  const T = (key) => TEXT[key][lang] || TEXT[key].uz;

  const L = (obj, prefix) => {
    if (!obj) return "";
    return obj[`${prefix}_${lang}`] || obj[`${prefix}_uz`] || "";
  };

  const mediaUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return `${BaseURL.replace(/\/$/, "")}${path.startsWith("/") ? "" : "/"}${path}`;
  };

  if (loading) {
    return <div className={classes.loading}>{T("loading")}</div>;
  }

  if (error || !goal) {
    return (
      <div className={classes.narrow}>
        <div className={classes.errorBox}>
          <h2 className={classes.errorTitle}>{T("notFoundTitle")}</h2>
          <p>{T("notFoundText")}</p>
        </div>
      </div>
    );
  }

  const title = L(goal, "header_title") || L(goal, "title");
  const heroImage = mediaUrl(goal.hero_image);
  const heroTagline = L(goal, "hero_tagline");
  const heroLead = L(goal, "hero_lead");
  const content = L(goal, "content");
  const contentHeading = L(goal, "content_heading");
  const author = L(goal, "author");
  // Blokda biror mazmun bo'lsa — chiziladi. Rasm va ustki yorliq ham
  // mazmun hisoblanadi: faqat rasmdan iborat blok ham ko'rinishi kerak.
  const sections = (goal.sections || []).filter(
    (s) =>
      L(s, "title") ||
      L(s, "lead") ||
      L(s, "tagline") ||
      L(s, "panel_title") ||
      L(s, "panel_body") ||
      L(s, "image_caption") ||
      Boolean(s.image) ||
      (s.items || []).length ||
      (s.highlights || []).length
  );

  // Ko'rinish ham, raqamlash ham MAZMUNDAN chiqariladi — admin tanlamaydi.
  // Birinchi blok kirish bo'lib raqamsiz turadi, qolganlari 01, 02, 03 …
  // Oxirgi blokda rasm, panel va element bo'lmasa — u yakuniy chaqiriq bo'ladi.
  const hasBody = (s) =>
    Boolean(s.image) ||
    Boolean(L(s, "panel_title")) ||
    Boolean(L(s, "panel_body")) ||
    (s.items || []).length > 0 ||
    (s.highlights || []).length > 0;

  let counter = 0;
  const numbers = sections.map((s, i) => (i === 0 ? null : ++counter));
  const closingIndex =
    sections.length > 1 && !hasBody(sections[sections.length - 1])
      ? sections.length - 1
      : -1;

  const renderSection = (section, index) => {
    const no = numbers[index];
    const tagline = L(section, "tagline");
    const secTitle = L(section, "title");
    const lead = L(section, "lead");
    const image = mediaUrl(section.image);
    const panelTitle = L(section, "panel_title");
    const panelEyebrow = L(section, "panel_eyebrow");
    const panelBody = paragraphs(L(section, "panel_body"));

    // Bo'sh qatorlar (sarlavhasi ham, matni ham yo'q) e'tiborga olinmaydi
    const items = (section.items || []).filter(
      (i) => L(i, "title") || L(i, "text")
    );
    const cards = items.filter((i) => i.kind === "card");
    const chips = items.filter((i) => i.kind === "chip" && L(i, "title"));
    const listItems = items.filter((i) => i.kind === "list");

    // Kengaytirilgan ko'rinishlar — element turi o'zi qaysi blok chizilishini
    // aytadi. Ma'nosi yo'q element (sonsiz ko'rsatkich, gapsiz iqtibos,
    // javobsiz savol) chizilmaydi.
    const stats = items.filter((i) => i.kind === "stat" && L(i, "title"));
    const features = items.filter((i) => i.kind === "feature" && L(i, "title"));
    const steps = items.filter((i) => i.kind === "step" && L(i, "title"));
    const quotes = items.filter((i) => i.kind === "quote" && L(i, "text"));
    const faqs = items.filter(
      (i) => i.kind === "faq" && L(i, "title") && L(i, "text")
    );

    // Ajratilgan kartalar — alohida ro'yxat, element turlaridan mustaqil.
    const highlights = (section.highlights || []).filter(
      (h) => L(h, "title") || L(h, "text") || h.value
    );

    const hasPanel = Boolean(panelTitle || panelBody.length);
    const hasImage = Boolean(image);

    // Toq raqamli bloklar to'q fonda — namunadagi ritm.
    const dark = Boolean(no) && no % 2 === 1 && index !== closingIndex;

    // --- yakuniy chaqiriq ---
    if (index === closingIndex) {
      return (
        <section key={section.id} className={classes.cta}>
          <div className={classes.ctaInner}>
            {no && <span className={classes.ctaNo}>{String(no).padStart(2, "0")}</span>}
            {tagline && <span className={classes.ctaTagline}>{tagline}</span>}
            {secTitle && <h2 className={classes.ctaTitle}>{secTitle}</h2>}
            {lead && <p className={classes.ctaLead}>{lead}</p>}
            {section.link_url && (
              <a className={classes.ctaBtn} href={section.link_url}>
                {T("more")}
                <span aria-hidden="true"> →</span>
              </a>
            )}
          </div>
        </section>
      );
    }

    const panelNode = (
      <div className={classes.panel}>
        {panelEyebrow && <span className={classes.panelEyebrow}>{panelEyebrow}</span>}
        {panelTitle && <h3 className={classes.panelTitle}>{panelTitle}</h3>}
        {panelBody.map((t, i) => (
          <p key={i} className={classes.panelText}>
            {t}
          </p>
        ))}
        {section.link_url && (
          <a className={classes.panelBtn} href={section.link_url}>
            {T("more")}
            <span aria-hidden="true"> ↗</span>
          </a>
        )}
      </div>
    );

    const imageNode = (
      <div className={classes.splitImage}>
        <img src={image} alt="" className={classes.splitImg} />
        {(L(section, "image_eyebrow") || L(section, "image_caption")) && (
          <div className={classes.splitCaption}>
            {L(section, "image_eyebrow") && (
              <span className={classes.splitCaptionEyebrow}>
                {L(section, "image_eyebrow")}
              </span>
            )}
            {L(section, "image_caption") && (
              <span className={classes.splitCaptionTitle}>
                {L(section, "image_caption")}
              </span>
            )}
          </div>
        )}
      </div>
    );

    const cardList = (cls) => (
      <div className={cls}>
        {cards.map((c, i) => (
          <div key={c.id} className={classes.numCard}>
            <span className={classes.numCardNo}>{String(i + 1).padStart(2, "0")}</span>
            <div>
              <h3 className={classes.numCardTitle}>{L(c, "title")}</h3>
              {L(c, "text") && <p className={classes.numCardText}>{L(c, "text")}</p>}
            </div>
          </div>
        ))}
      </div>
    );

    // --- Mazmunga qarab ko'rinish tanlanadi ---
    let body = null;
    let cardsUsed = false;

    if (hasImage && hasPanel) {
      // Rasm va rangli panel — yonma-yon
      body = (
        <div className={classes.splitRow}>
          {imageNode}
          {panelNode}
        </div>
      );
    } else if (hasPanel && cards.length > 0) {
      // Panel va kartalar — yonma-yon
      body = (
        <div className={classes.panelCards}>
          {panelNode}
          {cardList(classes.cardGrid)}
        </div>
      );
      cardsUsed = true;
    } else if (hasImage) {
      // Yolg'iz rasm — butun kenglikda, o'z qatorida.
      // Kartalar bo'lsa ular pastda alohida chiqadi (qo'shilib ketmaydi).
      body = (
        <div className={`${classes.splitRow} ${classes.splitRowSingle}`}>{imageNode}</div>
      );
    } else if (hasPanel) {
      body = (
        <div className={`${classes.splitRow} ${classes.splitRowSingle}`}>{panelNode}</div>
      );
    }

    return (
      <section
        key={section.id}
        className={`${classes.block} ${dark ? classes.blockDark : ""}`}
      >
        <div className={classes.blockInner}>
          <div className={classes.blockHead}>
            <div>
              {no && (
                <span className={classes.blockNo}>{String(no).padStart(2, "0")}</span>
              )}
              {tagline && <span className={classes.tagline}>{tagline}</span>}
              {secTitle && <h2 className={classes.blockTitle}>{secTitle}</h2>}
            </div>
            {lead && <p className={classes.blockLead}>{lead}</p>}
          </div>

          {body}

          {stats.length > 0 && <StatBand items={stats} L={L} />}

          {features.length > 0 && (
            <div className={classes.featureGrid}>
              {features.map((f, i) => (
                <article key={f.id} className={classes.featureCard}>
                  <span className={classes.featureGhost} aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className={classes.featureRule} aria-hidden="true" />
                  <h3 className={classes.featureTitle}>{L(f, "title")}</h3>
                  {L(f, "text") && (
                    <p className={classes.featureText}>{L(f, "text")}</p>
                  )}
                </article>
              ))}
            </div>
          )}

          {!cardsUsed && cards.length > 0 && cardList(classes.cardRow)}

          {steps.length > 0 && (
            <ol className={classes.timeline}>
              {steps.map((st, i) => (
                <li key={st.id} className={classes.timelineItem}>
                  <span className={classes.timelineRail} aria-hidden="true" />
                  <span className={classes.timelineDot}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className={classes.timelineTitle}>{L(st, "title")}</h3>
                  {L(st, "text") && (
                    <p className={classes.timelineText}>{L(st, "text")}</p>
                  )}
                </li>
              ))}
            </ol>
          )}

          {chips.length > 0 && (
            <ul className={classes.chipRow}>
              {chips.map((c) => (
                <li key={c.id} className={classes.chip}>
                  {L(c, "title")}
                </li>
              ))}
            </ul>
          )}

          {listItems.length > 0 && (
            <ol className={classes.numList}>
              {listItems.map((item, i) => (
                <li key={item.id} className={classes.numListItem}>
                  <span className={classes.numListNo}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>
                    <span className={classes.numListTitle}>{L(item, "title")}</span>
                    {L(item, "text") && (
                      <span className={classes.numListText}>{L(item, "text")}</span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          )}

          {quotes.length > 0 && (
            <div
              className={`${classes.quoteWrap} ${
                quotes.length === 1 ? classes.quoteWrapSolo : ""
              }`}
            >
              {quotes.map((q) => (
                <figure key={q.id} className={classes.quote}>
                  <span className={classes.quoteMark} aria-hidden="true">
                    “
                  </span>
                  <blockquote className={classes.quoteText}>
                    {L(q, "text")}
                  </blockquote>
                  {L(q, "title") && (
                    <figcaption className={classes.quoteBy}>
                      {L(q, "title")}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          )}

          {faqs.length > 0 && (
            <FaqList items={faqs} L={L} idBase={`blok-${section.id}`} />
          )}

          {highlights.length > 0 && <HighlightRow cards={highlights} L={L} />}

          {section.link_url && !hasPanel && (
            <a className={classes.blockLink} href={section.link_url}>
              {T("more")}
              <span aria-hidden="true"> ↗</span>
            </a>
          )}
        </div>
      </section>
    );
  };

  return (
    <div className={classes.page} style={accentVars(goal.color)}>
      {/* --------- Yuqori qism --------- */}
      <div className={classes.hero}>
        {heroImage && <img src={heroImage} alt="" className={classes.heroImg} />}
        <div className={classes.heroScrim} />

        <div className={classes.heroInner}>
          <nav className={classes.crumbs} aria-label="breadcrumb">
            <button type="button" className={classes.crumbLink} onClick={() => navigate("/")}>
              {T("home")}
            </button>
            <span aria-hidden="true">/</span>
            <button type="button" className={classes.crumbLink} onClick={() => navigate("/sdg")}>
              {T("sdg")} {goal.number}
            </button>
            <span aria-hidden="true">/</span>
            <span className={classes.crumbCurrent}>{L(goal, "title")}</span>
          </nav>

          {heroTagline && <span className={classes.heroTagline}>{heroTagline}</span>}
          <h1
            className={`${classes.heroTitle} ${
              title.length > 70 ? classes.heroTitleLong : ""
            }`}
          >
            {title}
          </h1>
          {heroLead && <p className={classes.heroLead}>{heroLead}</p>}
        </div>

        <div className={classes.badge}>
          <div>
            {goal.badge_prefix && (
              <span className={classes.badgePrefix}>{goal.badge_prefix}</span>
            )}
            <span className={classes.badgeTitle}>{L(goal, "title")}</span>
          </div>
          <span className={classes.badgeNo}>{String(goal.number).padStart(2, "0")}</span>
        </div>
      </div>

      {/* --------- Admin kiritgan bloklar --------- */}
      {sections.map(renderSection)}

      {/* --------- CKEditor mazmuni (eski maydon) --------- */}
      {(content || (sections.length === 0 && !heroLead)) && (
        <div className={classes.narrow}>
          {contentHeading && <h2 className={classes.blockTitle}>{contentHeading}</h2>}
          <div
            className={classes.richContent}
            dangerouslySetInnerHTML={{
              __html: content || `<p>${T("soon")}</p>`,
            }}
          />
          {(author || goal.publish_date) && (
            <div className={classes.authorMeta}>
              {author && <p className={classes.authorName}>{author}</p>}
              {goal.publish_date && <p className={classes.publishDate}>{goal.publish_date}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SDGDetail;
